import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";

/**
 * The named colors a calendar or event can carry, with the mapping from
 * provider color ids.
 */
export type LonaColors =
  | "red"
  | "yellow"
  | "orange"
  | "green"
  | "blue"
  | "teal"
  | "purple"
  | "gray";

/**
 * Values that go with `LonaColors`: `toColorId`, `fromCalendarColorId`,
 * `fromEventColorId`, `fromColorId`, `colorIdHex`, `hex`, and 1 more.
 */
export namespace LonaColors {
  // Google Calendar color IDs (1-24)
  // prettier-ignore
  const CalendarColorIdMap: { [k: number]: LonaColors } = {
    1:  "gray",    // Cocoa #795548
    2:  "red",     // Flamingo #E67C73
    3:  "red",     // Tomato #D50000
    4:  "orange",  // Tangerine #F4511E
    5:  "orange",  // Pumpkin #EF6C00
    6:  "yellow",  // Mango #F09300
    7:  "teal",    // Eucalyptus #009688
    8:  "green",   // Basil #0B8043
    9:  "green",   // Pistachio #7CB342
    10: "yellow",  // Avocado #C0CA33
    11: "yellow",  // Citron #E4C441
    12: "yellow",  // Banana #F6BF26
    13: "teal",    // Sage #33B679
    14: "blue",    // Peacock #039BE5
    15: "blue",    // Cobalt #4285F4
    16: "blue",    // Blueberry #3F51B5
    17: "purple",  // Lavender #7986CB
    18: "purple",  // Wisteria #B39DDB
    19: "gray",    // Graphite #616161
    20: "gray",    // Birch #A79B8E
    21: "red",     // Radicchio #AD1457
    22: "red",     // Cherry Blossom #D81B60
    23: "purple",  // Grape #8E24AA
    24: "purple",  // Amethyst #9E69AF
  };

  // Google Event color IDs (1-11)
  // prettier-ignore
  const EventColorIdMap: { [k: number]: LonaColors } = {
    1:  "purple",  // Lavender #7986CB
    2:  "teal",    // Sage #33B679
    3:  "purple",  // Grape #8E24AA
    4:  "red",     // Flamingo #E67C73
    5:  "yellow",  // Banana #F6BF26
    6:  "orange",  // Tangerine #F4511E
    7:  "blue",    // Peacock #039BE5
    8:  "gray",    // Graphite #616161
    9:  "blue",    // Blueberry #3F51B5
    10: "green",   // Basil #0B8043
    11: "red",     // Tomato #D50000
  };

  // Lona color → most representative Google Calendar color ID
  const CalendarReverseMap: Map<LonaColors, number> = new Map([
    ["red", 3],      // Tomato
    ["orange", 4],   // Tangerine
    ["yellow", 12],  // Banana
    ["green", 8],    // Basil
    ["blue", 14],    // Peacock
    ["teal", 7],     // Eucalyptus
    ["purple", 23],  // Grape
    ["gray", 19],    // Graphite
  ]);

  export function toColorId(color: LonaColors): number {
    return nonnull(
      CalendarReverseMap.get(color),
      `LonaColors.toColorId: unmapped color "${color}"`,
    );
  }

  export function fromCalendarColorId(n: Option<number>): Option<LonaColors> {
    if (!n) return null;
    if (Number.isNaN(n)) return null;
    return CalendarColorIdMap[n] ?? null;
  }

  export function fromEventColorId(n: Option<number>): Option<LonaColors> {
    if (!n) return null;
    if (Number.isNaN(n)) return null;
    return EventColorIdMap[n] ?? null;
  }

  // Alias — all existing call sites use calendar color IDs
  export const fromColorId = fromCalendarColorId;

  export function colorIdHex(n: Option<number>): Option<string> {
    const colorId = fromCalendarColorId(n);
    return colorId ? hex(colorId) : null;
  }

  // prettier-ignore
  const ALL: LonaColors[] = [
    "red",
    "yellow",
    "orange",
    "green",
    "blue",
    "teal",
    "purple",
    "gray",
  ];

  export function hex(n: LonaColors): string {
    return `var(--${n})`;
  }

  export function dimmed(n: LonaColors): string {
    return `var(--${n}-dimmed)`;
  }
}
